import { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { AscendHorizonLogo, Icon } from '@ascend/shared/components';
import { useWalletStore } from '../stores/useWalletStore';

// ─── Sidebar Navigation Config ───────────────────────────────────────────────

interface NavChild {
  label: string;
  hash?: string;
  path?: string;
  requiresAuth?: boolean;
  unauthLabel?: string;
}

interface NavItem {
  label: string;
  iconName: string;
  path: string;
  children?: NavChild[];
  requiresAuth?: boolean;
}

interface NavSection {
  heading: string | null;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    heading: null,
    items: [
      { label: 'Portfolio', iconName: 'streamline:interface-dashboard-layout-3-app-application-dashboard-home-layout', path: '/' },
    ],
  },
  {
    heading: 'BORROW',
    items: [
      { label: 'Credit Facilities', iconName: 'streamline:building-2', path: '/credit-facilities', requiresAuth: true },
      { label: 'Facility Access', iconName: 'streamline:shield-check', path: '/facility-access', requiresAuth: true },
    ],
  },
  {
    heading: 'LEND',
    items: [
      {
        label: 'Lender Dashboard', iconName: 'streamline:bank', path: '/lender/dashboard',
        children: [
          { label: 'Reserve & Protection', hash: 'reserve-protection' },
          { label: 'Your Positions', hash: 'positions', requiresAuth: true, unauthLabel: 'Available Pools' },
        ],
      },
    ],
  },
  {
    heading: 'LIQUIDATE',
    items: [
      {
        label: 'Liquidations', iconName: 'streamline:legal-justice-hammer-hammer-work-legal-mallet-office-company-gavel-justice-judge-arbitration-court', path: '/liquidations',
        children: [
          { label: 'Claim Settlement', path: '/liquidations/claim/settlement', requiresAuth: true },
          { label: 'History', path: '/liquidations/history', requiresAuth: true },
        ],
      },
    ],
  },
  {
    heading: 'ACCOUNT',
    items: [
      { label: 'Transaction History', iconName: 'streamline:interface-time-clock-circle-clock-loading-measure-time-circle', path: '/history', requiresAuth: true },
      {
        label: 'Profile', iconName: 'streamline:interface-user-circle-circle-geometric-human-person-single-user', path: '/profile', requiresAuth: true,
        children: [
          { label: 'Wallet Identity', hash: 'wallet' },
          { label: 'Portfolio Overview', hash: 'overview' },
          { label: 'Onchain Identity', hash: 'identity' },
          { label: 'Onchain Claims', hash: 'claims' },
          { label: 'Token Balances', hash: 'balances' },
        ],
      },
    ],
  },
];

function isActive(pathname: string, itemPath: string): boolean {
  if (itemPath === '/') return pathname === '/';
  return pathname === itemPath || pathname.startsWith(itemPath + '/');
}

// ─── Sidebar Content ─────────────────────────────────────────────────────────

function scrollToHash(hash: string) {
  const el = document.getElementById(hash);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function SidebarNav({ onNavigate, isConnected }: { onNavigate?: () => void; isConnected: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();

  function handleChildClick(child: NavChild, parentPath: string) {
    if (child.path) {
      navigate(child.path);
      onNavigate?.();
      return;
    }
    if (child.hash) {
      const isOnParent = isActive(location.pathname, parentPath);
      if (isOnParent) {
        scrollToHash(child.hash);
      } else {
        navigate(parentPath);
        setTimeout(() => scrollToHash(child.hash!), 100);
      }
    }
    onNavigate?.();
  }

  // Check if parent is active, considering child paths too
  function isItemActive(item: NavItem): boolean {
    if (isActive(location.pathname, item.path)) return true;
    return item.children?.some((c) => c.path && isActive(location.pathname, c.path)) ?? false;
  }

  return (
    <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
      {NAV_SECTIONS.map((section, sIdx) => {
        const visibleItems = section.items.filter((item) => !item.requiresAuth || isConnected);
        if (visibleItems.length === 0) return null;
        return (
          <div key={sIdx} className={sIdx > 0 ? 'pt-4' : ''}>
            {section.heading && (
              <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                {section.heading}
              </div>
            )}
            {visibleItems.map((item) => {
              const active = isItemActive(item);
              const visibleChildren = item.children
                ?.filter((c) => !c.requiresAuth || isConnected || c.unauthLabel)
                .map((c) => (!isConnected && c.unauthLabel ? { ...c, label: c.unauthLabel } : c));
              return (
                <div key={item.path + item.label}>
                  <Link
                    to={item.path}
                    onClick={onNavigate}
                    className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${
                      active
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                    }`}
                  >
                    <Icon name={item.iconName} className="h-4 w-4 flex-shrink-0" />
                    {item.label}
                  </Link>
                  {visibleChildren && visibleChildren.length > 0 && active && (
                    <div className="ml-7 mt-0.5 space-y-0.5 border-l border-border pl-3">
                      {visibleChildren.map((child) => {
                        const childActive = child.path ? isActive(location.pathname, child.path) : false;
                        return (
                          <button
                            key={child.hash ?? child.path ?? child.label}
                            onClick={() => handleChildClick(child, item.path)}
                            className={`block w-full rounded-md px-2 py-1.5 text-left text-xs transition-colors ${
                              childActive
                                ? 'text-primary font-medium'
                                : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                            }`}
                          >
                            {child.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </nav>
  );
}

// ─── Layout Component ────────────────────────────────────────────────────────

const AUTH_ROUTES = ['/credit-facilities', '/facility-access', '/history', '/profile', '/liquidations/claim', '/liquidations/history'];

export function ClientLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isConnected, address, balance, connect, disconnect } = useWalletStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Redirect away from auth-required routes on disconnect
  useEffect(() => {
    if (!isConnected && AUTH_ROUTES.some((r) => location.pathname.startsWith(r))) {
      navigate('/', { replace: true });
    }
  }, [isConnected, location.pathname, navigate]);

  const walletSection = (
    <div className="border-t border-border px-3 py-4 space-y-3">
      {isConnected ? (
        <>
          <Link
            to="/profile"
            className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm transition-colors hover:bg-accent w-full"
          >
            <span className="font-mono text-xs truncate">{address}</span>
            <span className="hidden lg:inline text-xs text-muted-foreground">{balance}</span>
            <span className="h-2 w-2 rounded-full bg-green-500 flex-shrink-0" />
            <Icon name="streamline:interface-arrows-button-down-arrow-down-keyboard" className="h-3.5 w-3.5 text-muted-foreground ml-auto flex-shrink-0" />
          </Link>
          <button
            onClick={disconnect}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <Icon name="streamline:logout-1" className="h-4 w-4" />
            Disconnect
          </button>
        </>
      ) : (
        <button
          onClick={connect}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="h-4 w-4" />
          Connect Wallet
        </button>
      )}
    </div>
  );

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* ─── Desktop Sidebar ──────────────────────────────────────────────── */}
      <aside className="hidden md:flex md:w-60 flex-col border-r border-border bg-background sticky top-0 h-screen">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
          <Link to="/" className="flex items-center gap-2">
            <AscendHorizonLogo size={24} className="text-primary" />
          </Link>
        </div>

        <SidebarNav isConnected={isConnected} />

        {walletSection}
      </aside>

      {/* ─── Mobile Sidebar Overlay ───────────────────────────────────────── */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          <aside className="fixed inset-y-0 left-0 z-50 w-72 flex flex-col border-r border-border bg-background md:hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <Link to="/" className="flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <AscendHorizonLogo size={24} className="text-primary" />
              </Link>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="rounded-md p-1.5 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="streamline:interface-delete-2-remove-bold-add-button-buttons-delete" className="h-5 w-5" />
              </button>
            </div>

            <SidebarNav isConnected={isConnected} onNavigate={() => setMobileMenuOpen(false)} />

            {walletSection}
          </aside>
        </>
      )}

      {/* ─── Main Content ─────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 border-b border-border bg-background">
          <div className="flex h-14 items-center justify-between px-4 sm:px-6">
            <div className="flex items-center gap-3 md:hidden">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="rounded-md p-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="streamline:interface-setting-menu-1-button-parallel-horizontal-lines-menu-navigation-three-hamburger" className="h-5 w-5" />
              </button>
              <Link to="/" className="flex items-center gap-2">
                <AscendHorizonLogo size={22} className="text-primary" />
              </Link>
            </div>

            <div className="hidden md:block">
              <span className="rounded-full border border-primary/30 bg-primary/5 px-3 py-0.5 text-xs font-medium text-primary">
                For Internal Use Only
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button className="relative rounded-md p-2 text-muted-foreground hover:text-foreground transition-colors">
                <Icon name="streamline:interface-alert-alarm-bell-2-alert-bell-ring-notification-alarm" className="h-4 w-4" />
              </button>

              {isConnected && (
                <Link
                  to="/profile"
                  className="md:hidden flex items-center gap-1.5 rounded-lg border border-border px-2 py-1.5 text-xs"
                >
                  <span className="font-mono">{address?.slice(0, 6)}…</span>
                  <span className="h-2 w-2 rounded-full bg-green-500" />
                </Link>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6 sm:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
